import { useCallback, useEffect, useRef, useState } from 'react';
import conversationService from '@/services/conversationService';
import { ApiError } from '@/services/apiService';
import {
  applyCallLive,
  applyCallPartial,
  applyCallStep,
  applyCallTurn,
  createLiveCallState,
  mergeCallRefresh,
  type CallLiveEvent,
  type CallPartialEvent,
  type CallStepEvent,
  type CallTurnEvent,
  type LiveCallState,
} from '@/lib/liveCall';
import { createMutationClock, fetchUnlessOvertaken } from '@/lib/mutationClock';
import { serverNow } from '@/lib/serverClock';
import { useDegradedPolling, useLiveEvent } from '@/hooks/useLiveEvents';

export interface LiveCallHook {
  state: LiveCallState | null;
  loading: boolean;
  error: string | null;
  /** Ended, but the stored transcript did not show up in time. */
  storedMissing: boolean;
  reload: () => void;
}

/** After the end the stored transcript (`conversation_id`) is asked for this often … */
export const STORED_POLL_INTERVAL_MS = 5_000;
/** … for at most this long (call-record normally lands within seconds). */
export const STORED_POLL_ATTEMPTS = 24;

/**
 * A running phone call as it happens (livecenter §3/§5): the transcript so
 * far from `GET /api/live/calls/{sid}`, then `call.turn` / `call.partial` /
 * `call.step` / `call.live` of this `call_sid` only.
 */
export function useLiveCall(callSid: string): LiveCallHook {
  const [state, setState] = useState<LiveCallState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mutations] = useState(createMutationClock);
  const [storedMissing, setStoredMissing] = useState(false);
  const seqRef = useRef(0);
  // Events during the first load go on top of its answer (reducers are idempotent).
  const loadedRef = useRef(false);
  const pendingRef = useRef<Array<(state: LiveCallState) => LiveCallState>>([]);

  const load = useCallback(
    async (silent: boolean) => {
      const seq = silent ? seqRef.current : ++seqRef.current;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const fetchCall = () => conversationService.getLiveCall(callSid);
      try {
        const res = silent ? await fetchUnlessOvertaken(mutations, fetchCall) : await fetchCall();
        if (res === null || seq !== seqRef.current) return;
        const now = serverNow();
        if (silent) {
          setState((prev) => (prev ? mergeCallRefresh(prev, res, now) : createLiveCallState(res, now)));
          setError(null);
        } else {
          const buffered = pendingRef.current;
          pendingRef.current = [];
          loadedRef.current = true;
          setState(buffered.reduce((state, apply) => apply(state), createLiveCallState(res, now)));
        }
      } catch (e) {
        if (silent || seq !== seqRef.current) return;
        pendingRef.current = [];
        setError(
          e instanceof ApiError && e.status === 404
            ? 'Dieser Anruf wurde nicht gefunden.'
            : 'Anruf konnte nicht geladen werden.',
        );
        setState(null);
      } finally {
        if (!silent && seq === seqRef.current) setLoading(false);
      }
    },
    [callSid, mutations],
  );

  useEffect(() => {
    setState(null);
    setStoredMissing(false);
    loadedRef.current = false;
    pendingRef.current = [];
    load(false);
  }, [load]);

  const apply = (fn: (prev: LiveCallState) => LiveCallState) => {
    mutations.tick();
    if (!loadedRef.current) {
      pendingRef.current.push(fn);
      return;
    }
    setState((prev) => (prev ? fn(prev) : prev));
  };

  // Ended without a stored transcript yet: `call.live` with the
  // conversation_id normally follows within seconds; if that event is lost,
  // ask the server a few times instead of saying "wird gespeichert" forever.
  const ended = state?.call.status === 'ended';
  const stored = state?.call.conversation_id != null;
  useEffect(() => {
    if (!ended || stored) return undefined;
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      if (attempts > STORED_POLL_ATTEMPTS) {
        clearInterval(id);
        setStoredMissing(true);
        return;
      }
      load(true);
    }, STORED_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [ended, stored, load]);

  useLiveEvent<CallTurnEvent>('call.turn', (event) => {
    if (event?.call_sid !== callSid) return;
    apply((prev) => applyCallTurn(prev, event));
  });
  useLiveEvent<CallPartialEvent>('call.partial', (event) => {
    if (event?.call_sid !== callSid) return;
    const now = serverNow();
    apply((prev) => applyCallPartial(prev, event, now));
  });
  useLiveEvent<CallStepEvent>('call.step', (event) => {
    if (event?.call_sid !== callSid) return;
    apply((prev) => applyCallStep(prev, event));
  });
  useLiveEvent<CallLiveEvent>('call.live', (event) => {
    if (event?.call_sid !== callSid) return;
    apply((prev) => applyCallLive(prev, event));
  });

  const refreshSilently = () => {
    load(true);
  };
  useLiveEvent('ready', refreshSilently);
  useDegradedPolling(refreshSilently);

  const reload = useCallback(() => {
    load(false);
  }, [load]);

  return { state, loading, error, storedMissing, reload };
}

export default useLiveCall;
